import { AnimatePresence } from "framer-motion";
import { CheckSquare } from "lucide-react";
import type { TaskRead, TaskStatusConfig } from "@/api/types";
import type { StatusPalette } from "./statusPalette";
import { TaskItem } from "./TaskItem";

interface TaskListProps {
  tasks: TaskRead[];
  projectSlug: string;
  showCompany?: boolean;
  showAssignee?: boolean;
  /** Label each row with its project (cross-project "My Tasks" feed).
   *  Reads `project_name` off the row when present. */
  showProjectBadge?: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onToggleComplete: (task: TaskRead) => Promise<void> | void;
  onEdit?: (task: TaskRead) => void;
  onDelete?: (task: TaskRead) => void;
  onOpenDetail?: (task: TaskRead) => void;
  fadingIds?: ReadonlySet<number>;
  emptyTitle?: string;
  emptyHint?: string;
  /** Catalog lookup — resolves a task's status key to its config so
   *  each row can render the right colored status badge. */
  getStatusConfig?: (
    key: string | null | undefined,
  ) => TaskStatusConfig | null;
  /** Palette used by the round done checkbox; same value for every
   *  row, so the page resolves it once and forwards. */
  donePalette?: StatusPalette;
}

export function TaskList({
  tasks,
  projectSlug,
  showCompany = true,
  showAssignee = true,
  showProjectBadge = false,
  canEdit,
  canDelete,
  onToggleComplete,
  onEdit,
  onDelete,
  onOpenDetail,
  fadingIds,
  emptyTitle = "Nothing here",
  emptyHint,
  getStatusConfig,
  donePalette,
}: TaskListProps) {
  if (tasks.length === 0) {
    return (
      <div className="rounded-plane bg-white px-6 py-16 text-center shadow-plane">
        <CheckSquare className="mx-auto mb-4 h-10 w-10 text-slate-300" />
        <p className="text-base font-medium text-slate-700">{emptyTitle}</p>
        {emptyHint && (
          <p className="mt-1 text-sm text-slate-500">{emptyHint}</p>
        )}
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-plane bg-white shadow-plane">
      <AnimatePresence initial={false} mode="popLayout">
        {tasks.map((task, idx) => (
          <TaskItem
            key={task.id}
            index={idx}
            task={task}
            projectSlug={projectSlug}
            showCompany={showCompany}
            showAssignee={showAssignee}
            canEdit={canEdit}
            canDelete={canDelete}
            onToggleComplete={onToggleComplete}
            onEdit={onEdit}
            onDelete={onDelete}
            onOpenDetail={onOpenDetail}
            fadingOut={fadingIds?.has(task.id)}
            statusConfig={getStatusConfig?.(task.status)}
            donePalette={donePalette}
            projectBadge={
              showProjectBadge ? <ProjectBadge task={task} /> : undefined
            }
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

/** Small pill naming the project a cross-project row belongs to.
 *  No-op when the row has no `project_name` (per-project feed). */
function ProjectBadge({ task }: { task: TaskRead }) {
  const name = (task as { project_name?: string }).project_name;
  if (!name) return null;
  return (
    <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">
      {name}
    </span>
  );
}

export function TaskListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="overflow-hidden rounded-plane bg-white shadow-plane">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b border-slate-900/[0.06] px-4 py-3 last:border-b-0"
          style={{ minHeight: 64 }}
        >
          <div className="h-6 w-6 animate-pulse rounded-full bg-slate-200" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/2 animate-pulse rounded bg-slate-200" />
            <div className="h-2.5 w-1/3 animate-pulse rounded bg-slate-100" />
          </div>
          <div className="h-5 w-24 animate-pulse rounded-full bg-slate-100" />
          <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-slate-200" />
        </div>
      ))}
    </div>
  );
}
